/**
 * Controller for managing the preferences modal of a carousel.
 * This class handles the initialization, preferences editing, and deletion of carousel elements.
 */
class CarouselPreferencesModalController {
    /**
     * Constructs the CarouselPreferencesModalController.
     * @param {Object} $scope - The AngularJS scope.
     * @param {Object} $uibModalInstance - The instance of the modal.
     * @param {Array} carousels - The list of carousels.
     * @param {Array} rows - The rows associated with the carousels.
     * @param {Object} contentObj - The content object containing carousel data.
     * @param {Object} $signals - The signals service for event handling.
     * @param {Object} TaskDataService - The service for task data operations.
     * @param {Object} $uibModal - The modal service for opening new modals.
     * @param {Object} DeleteObjectHelperService - The service for deleting objects.
     * @param {Object} TaskInfoService - The service for retrieving task information.
     */
    constructor($scope, $uibModalInstance, carousels, rows, contentObj, $signals, TaskDataService, $uibModal, DeleteObjectHelperService, TaskInfoService) {
        this.$scope = $scope;
        this.$uibModalInstance = $uibModalInstance;
        this.carousels = carousels;
        this.rows = rows;
        this.contentObj = contentObj;
        this.$signals = $signals;
        this.TaskDataService = TaskDataService;
        this.$uibModal = $uibModal;
        this.DeleteObjectHelperService = DeleteObjectHelperService;
        this.TaskInfoService = TaskInfoService;

        this._taskContext = TaskDataService.fetchContext();
        this.taskInfo = TaskInfoService.getTaskInfo();
        this.$scope.writingDirection = this.taskInfo.getInputLanguage().writingDirection;

        this.$scope.carousels = angular.copy(contentObj.data.carousels);
        this.$scope.carouselContentObj = {};
        this.$scope.showPreferences = false;
        this.deleteSlideObjects = [];

        this.init();
    }

    /**
     * Initializes the carousel preferences modal.
     * This method sets up the initial state of the modal, including copying and processing carousel data.
     */
    init() {
        this.$scope.carouselContentObj = {};
        angular.forEach(this.$scope.carousels, (carousel, carouselKey) => {
            this.$scope.carouselContentObj[carousel.content] = angular.copy(this._taskContext.read(carousel.content));
            delete carousel.popoverText;
        });

        this.deleteSlideObjects = [];
    }

    /**
     * Opens the preferences for a specific carousel.
     * @param {number} index - The index of the carousel to open preferences for.
     */
    openPreferences(index) {
        if (this.$scope.activeCarouselEdit === index) {
            this.$scope.activeCarouselEdit = -1;
        } else {
            this.$scope.activeCarouselEdit = index;
        }
    }

    /**
     * Handles the cancel button action.
     * This method dismisses the modal without saving changes.
     */
    onCancelButton() {
        this.$uibModalInstance.dismiss('cancel');
    }

    /**
     * Handles the confirmation action.
     * This method updates the carousel data, deletes necessary objects, and emits a signal to re-render rows.
     */
    onConfirm() {
        this.contentObj.data.carousels = this.$scope.carousels;
        angular.forEach(this.$scope.carouselContentObj, (carousel, carouselKey) => {
            this._taskContext.update(carouselKey, carousel.data);
        });
        angular.forEach(this.deleteSlideObjects, (containerEntry, key) => {
            this.DeleteObjectHelperService.deleteSubcontainerByEntry(containerEntry);
        });

        this.$signals.emit("rendering_rows", this.rows);
        this.$uibModalInstance.dismiss('cancel');
    }

    /**
     * Handles the deletion of a carousel.
     * @param {string} carouselUUID - The UUID of the carousel to be deleted.
     */
    onDeleteCarousel(carouselUUID) {
        let cancelForeach = false;
        angular.forEach(this.$scope.carousels, (carousel, carouselKey) => {
            if (carousel.content === carouselUUID && !cancelForeach) {
                this.contentObj.flagAsDirty();
                let _carouselContent = this._taskContext.read(carousel.content);
                if (Object.keys(_carouselContent.getObjects()).length > 0) {
                    let deleteCarouselModalInstance = this.$uibModal.open({
                        templateUrl: "/views/ib/components/carousel/modal/deleteCarouselModal.html",
                        windowClass: "carousel-delete",
                        keyboard: false,
                        backdrop: 'static',
                        controller: "DeleteCarouselModalController",
                    });

                    deleteCarouselModalInstance.result.then(() => {
                    }, () => {
                        if (deleteCarouselModalInstance.delete) {
                            this.deleteSlideObjects.push(this._taskContext.read(this.$scope.carousels[carouselKey].content));
                            this.$scope.carousels.splice(carouselKey, 1);
                        }
                    });

                } else {
                    this.deleteSlideObjects.push(this._taskContext.read(this.$scope.carousels[carouselKey].content));
                    this.$scope.carousels.splice(carouselKey, 1);
                }
                cancelForeach = true;
                return true;
            }
        });
    }
}

export default CarouselPreferencesModalController;